/*
  Standortverwaltung.

  Die Verwaltung lag früher als Abschnitt "Standorte" in den Einstellungen.
  Weil man sich in den Einstellungen immer *innerhalb* eines Standorts befindet,
  hängt sie jetzt am Standortwähler in der Topbar. Fachlich ist es dieselbe
  Logik wie vorher: dieselben Endpunkte, dieselbe Deaktivier-Bestätigung,
  dasselbe Erfassungsformular.
*/

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { API_BASE_URL } from "../../config";
import { AuthContext } from "../../AuthContext";
import useToast from "../../components/useToast";
import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppChip from "../../design-system/components/AppChip";
import AppConfirmDialog from "../../design-system/components/AppConfirmDialog";
import AppDialog from "../../design-system/components/AppDialog";
import AppDialogBody from "../../design-system/components/AppDialogBody";
import AppDialogFooter from "../../design-system/components/AppDialogFooter";
import AppDialogHeader from "../../design-system/components/AppDialogHeader";
import AppField from "../../design-system/components/AppField";
import AppFieldStack from "../../design-system/components/AppFieldStack";
import AppInput from "../../design-system/components/AppInput";
import AppLoadingOverlay from "../../design-system/components/AppLoadingOverlay";
import AppPersonBadge from "../../design-system/components/AppPersonBadge";
import AppSelect from "../../design-system/components/AppSelect";
import AppSwitch from "../../design-system/components/AppSwitch";
import AppTooltip from "../../design-system/components/AppTooltip";
import {
  IconAdd,
  IconArchive,
  IconDelete,
  IconEdit,
  IconRoom,
  IconSettings,
  IconSuccess,
} from "../../design-system/icons";
import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";
import "./settings.css";

let temporaryRowId = 0;

const emptyLocation = () => {
  temporaryRowId += 1;
  return {
    id: null,
    client_id: `location-${temporaryRowId}`,
    name: "",
    is_active: true,
    user_ids: [],
  };
};

const stripClientFields = (items) => (items || []).map(({ client_id, ...item }) => item);

export default function LocationsDialog({ open, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = localStorage.getItem("session_token");
  const { success, error } = useToast();
  const { selectedLocationId, setSelectedLocationId, syncLocations } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState(null);
  const [locations, setLocations] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [formError, setFormError] = useState("");
  const [deactivateIndex, setDeactivateIndex] = useState(null);

  const canEdit = Boolean(company?.can_edit_locations);
  const users = useMemo(() => company?.users || [], [company]);
  const userNameById = useMemo(
    () => new Map(users.map((user) => [Number(user.id), user.name || user.email || `User ${user.id}`])),
    [users]
  );

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/web/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompany(res.data || {});
      setLocations(res.data?.locations || []);
      syncLocations(res.data?.locations || [], res.data?.location?.id);
    } catch (err) {
      error("Standorte konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [error, syncLocations, token]);

  useEffect(() => {
    if (!open) return;
    setEditIndex(null);
    setFormError("");
    setArchiveOpen(false);
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const visibleRows = locations
    .map((location, index) => ({ location, index }))
    .filter(({ location }) => location.is_active !== false || archiveOpen);
  const archivedCount = locations.filter((location) => location.is_active === false).length;
  const editedLocation = Number.isInteger(editIndex) ? locations[editIndex] : null;

  const updateLocation = (index, key, value) => {
    const nextValue = key === "user_ids" ? (value || []).map(Number) : value;
    setLocations((current) =>
      current.map((location, itemIndex) =>
        itemIndex === index ? { ...location, [key]: nextValue } : location
      )
    );
  };

  const persist = async (nextLocations, { closeEditor = true } = {}) => {
    setSaving(true);
    setFormError("");
    try {
      await axios.put(`${API_BASE_URL}/api/web/settings/locations`, stripClientFields(nextLocations), {
        headers: { Authorization: `Bearer ${token}` },
      });
      success("Änderungen gespeichert.");
      if (closeEditor) setEditIndex(null);
      await fetchLocations();
      return true;
    } catch (err) {
      setFormError(err?.response?.data?.message || "Änderungen konnten nicht gespeichert werden.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const startNewLocation = () => {
    const next = [...locations, emptyLocation()];
    setLocations(next);
    setFormError("");
    setEditIndex(next.length - 1);
  };

  const cancelEditor = async () => {
    setEditIndex(null);
    setFormError("");
    await fetchLocations();
  };

  const selectLocation = (location) => {
    if (!location?.id) return;
    setSelectedLocationId(String(location.id));
  };

  const openLocationSettings = (location) => {
    selectLocation(location);
    onClose?.();
    navigate("/settings");
  };

  const confirmDeactivate = async () => {
    if (!Number.isInteger(deactivateIndex)) return;
    const next = locations.map((location, index) =>
      index === deactivateIndex ? { ...location, is_active: false, user_ids: [] } : location
    );
    setDeactivateIndex(null);
    setLocations(next);
    await persist(next, { closeEditor: false });
  };

  const requestDeactivate = (index) => {
    const location = locations[index];
    if (!location?.id) {
      setLocations((current) => current.filter((_, itemIndex) => itemIndex !== index));
      return;
    }
    setDeactivateIndex(index);
  };

  return (
    <>
      <AppDialog open={open} onClose={onClose} size="lg">
        <AppDialogHeader
          title="Standorte verwalten"
          subtitle="Wähle einen Standort aus, öffne dessen Einstellungen oder lege einen neuen an."
          visual={<IconRoom />}
          action={canEdit && !editedLocation ? (
            <div className="company-section-view__actions">
              {archivedCount > 0 || archiveOpen ? (
                <AppButton
                  variant="muted"
                  size="sm"
                  onClick={() => setArchiveOpen((current) => !current)}
                  aria-expanded={archiveOpen}
                >
                  <IconArchive />
                  {archiveOpen
                    ? t("settings_ui.hide_location_archive")
                    : t("settings_ui.location_archive_count", { count: archivedCount })}
                </AppButton>
              ) : null}
              <AppButton variant="primary" size="sm" onClick={startNewLocation}>
                <IconAdd />
                Standort hinzufügen
              </AppButton>
            </div>
          ) : null}
        />
        <AppDialogBody>
          {loading ? <AppLoadingOverlay open scope="contained" /> : null}

          {formError ? <AppCallout variant="error" description={formError} /> : null}

          {editedLocation ? (
            <AppFieldStack>
              <AppField label={t("settings_ui.location_name")} required>
                <AppInput
                  value={editedLocation.name || ""}
                  onChange={(event) => updateLocation(editIndex, "name", event.target.value)}
                />
              </AppField>
              <AppField
                label={`Mitarbeitende (${editedLocation.user_ids?.length || 0})`}
                help="Suche und wähle die Mitarbeitenden dieses Standorts aus."
              >
                <AppSelect
                  options={users.map((user) => ({
                    value: user.id,
                    label: user.name || user.email || `User ${user.id}`,
                  }))}
                  multiple
                  searchable
                  showSelectAll
                  value={editedLocation.user_ids || []}
                  onChange={(event) => updateLocation(editIndex, "user_ids", event.target.value)}
                  disabled={!editedLocation.is_active || users.length === 0}
                  placeholder={t("settings_ui.add_person")}
                  searchPlaceholder="Person suchen"
                />
              </AppField>
              <div className="company-location-status-field">
                <div className="company-location-status-field__copy">
                  <strong>{t("settings_ui.status")}</strong>
                  <span>{t("settings_ui.location_inactive_info")}</span>
                </div>
                <AppSwitch
                  aria-label={editedLocation.is_active ? "Standort deaktivieren" : "Standort aktivieren"}
                  checked={Boolean(editedLocation.is_active)}
                  onChange={(event) => {
                    if (event.target.checked || !editedLocation.id) {
                      updateLocation(editIndex, "is_active", event.target.checked);
                      if (!event.target.checked) updateLocation(editIndex, "user_ids", []);
                      return;
                    }
                    setDeactivateIndex(editIndex);
                  }}
                />
              </div>
            </AppFieldStack>
          ) : visibleRows.length ? (
            <div className="company-location-list">
              {visibleRows.map(({ location, index }) => {
                const assignedNames = (location.user_ids || [])
                  .map((userId) => userNameById.get(Number(userId)))
                  .filter(Boolean);
                const isSelected = String(location.id) === String(selectedLocationId);

                return (
                  <div className="company-location-row" key={location.id || location.client_id || index}>
                    <div className="company-location-row__name">
                      <strong>{location.name || "Unbenannter Standort"}</strong>
                      <span>{assignedNames.length} Mitarbeitende</span>
                    </div>
                    <div className="company-location-row__people" aria-label={`${assignedNames.length} Mitarbeitende`}>
                      {assignedNames.slice(0, 3).map((name, personIndex) => (
                        <AppPersonBadge
                          className="company-location-avatar"
                          key={`${name}-${personIndex}`}
                          name={name}
                          size="lg"
                          tone="brand"
                          title={name}
                        />
                      ))}
                      {assignedNames.length > 3 ? (
                        <AppPersonBadge
                          className="company-location-avatar company-location-avatar--more"
                          initials={`+${assignedNames.length - 3}`}
                          size="lg"
                        />
                      ) : null}
                      {!assignedNames.length ? (
                        <span className="company-location-row__empty">{t("settings_ui.no_employees")}</span>
                      ) : null}
                    </div>
                    <div className="company-location-row__status">
                      {location.is_active === false ? (
                        <AppChip variant="muted" appearance="outlined" size="md">
                          {t("settings_ui.inactive")}
                        </AppChip>
                      ) : isSelected ? (
                        <AppChip variant="primary" appearance="semiFilled" size="md" leadingIcon={<IconSuccess />}>
                          Aktiv
                        </AppChip>
                      ) : null}
                    </div>
                    <div className="company-location-row__actions">
                      {location.is_active !== false && !isSelected ? (
                        <AppButton variant="outlined" size="sm" onClick={() => selectLocation(location)}>
                          Auswählen
                        </AppButton>
                      ) : null}
                      <AppTooltip content="Einstellungen öffnen">
                        <AppButton
                          variant="neutral"
                          size="sm"
                          iconOnly
                          aria-label="Einstellungen öffnen"
                          disabled={location.is_active === false}
                          onClick={() => openLocationSettings(location)}
                        >
                          <IconSettings />
                        </AppButton>
                      </AppTooltip>
                      {canEdit ? (
                        <>
                          <AppTooltip content="Bearbeiten">
                            <AppButton
                              variant="neutral"
                              size="sm"
                              iconOnly
                              aria-label="Standort bearbeiten"
                              onClick={() => {
                                setFormError("");
                                setEditIndex(index);
                              }}
                            >
                              <IconEdit />
                            </AppButton>
                          </AppTooltip>
                          <AppTooltip content="Entfernen">
                            <AppButton
                              variant="error"
                              size="sm"
                              iconOnly
                              aria-label="Standort entfernen"
                              disabled={location.is_active === false}
                              onClick={() => requestDeactivate(index)}
                            >
                              <IconDelete />
                            </AppButton>
                          </AppTooltip>
                        </>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <AppCallout
              variant="info"
              title={t("settings_ui.no_locations")}
              description={t("settings_ui.no_locations_description")}
            />
          )}
        </AppDialogBody>
        <AppDialogFooter>
          {editedLocation ? (
            <>
              <AppButton variant="neutral" onClick={cancelEditor} disabled={saving}>
                {t("settings_ui.cancel_edit")}
              </AppButton>
              <AppButton
                variant="primary"
                onClick={() => persist(locations)}
                disabled={saving || !String(editedLocation.name || "").trim()}
              >
                {saving ? t("settings_ui.saving_changes") : t("settings_ui.save_changes")}
              </AppButton>
            </>
          ) : (
            <AppButton variant="neutral" onClick={onClose}>
              {t("button.close", "Schliessen")}
            </AppButton>
          )}
        </AppDialogFooter>
      </AppDialog>

      <AppConfirmDialog
        open={Number.isInteger(deactivateIndex)}
        onClose={() => setDeactivateIndex(null)}
        onConfirm={confirmDeactivate}
        title="Standort deaktivieren?"
        description="Der Standort ist danach nicht mehr in der Standortauswahl verfügbar. Zugeordnete Mitarbeitende können diesen Standort nicht mehr aktiv auswählen."
        cancelLabel={t("button.cancel")}
        confirmLabel={t("button.delete")}
        confirmTone="error"
        confirmIcon={<IconDelete />}
      />
    </>
  );
}
